import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useGetBadge } from "../../api/hooks/useBadge";
import { CRITERIA_TYPE_LABELS } from "../../constants/badge";
import type { Badge } from "../../api/types/badge.types";
import { CircularProgress, Box } from "@mui/material";

const ViewBadge = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const badgeData = (location.state && location.state.badge) as Badge | undefined;
  const badgeId = badgeData?._id;

  // Fetch badge data from API if we have an ID
  const { data: badgeDetailData, isLoading: isLoadingBadge } = useGetBadge(
    badgeId || '',
    { enabled: !!badgeId }
  );

  // Use API data if available, otherwise use location state data
  const badge = badgeDetailData?.data || badgeData;

  // Helper function to get criteria type label
  const getCriteriaTypeLabel = (badge: Badge | undefined): string => {
    if (!badge) return "-";
    const type = badge.type ?? badge.criteriaType;
    if (!type) return "-";
    
    if (typeof type === "number") {
      return CRITERIA_TYPE_LABELS[type] || "Unknown";
    }
    return String(type);
  };

  // Helper function to get target value
  const getTargetValue = (badge: Badge | undefined): string => {
    if (!badge) return "-";
    const value = badge.value ?? badge.targetValue;
    return value?.toString() || "-";
  };

  // Format date helper
  const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "-";
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      return "-";
    }
  };

  if (isLoadingBadge) {
    return (
      <div className="view-badge-page">
        <div className="main_title">
          <h1>Badge Details</h1>
          <p>
            <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
            <button onClick={() => navigate("/achievements")}>
              Manage Achievements & Badges
            </button>{" "}
            - View Badge
          </p>
        </div>
        <div className="cards">
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        </div>
      </div>
    );
  }

  if (!badge) {
    return (
      <div className="view-badge-page">
        <div className="main_title">
          <h1>Badge Details</h1>
          <p>
            <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
            <button onClick={() => navigate("/achievements")}>
              Manage Achievements & Badges
            </button>{" "}
            - View Badge
          </p>
        </div>
        <div className="cards">
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <p>Badge not found</p>
          </Box>
        </div>
      </div>
    );
  }

  return (
    <div className="view-badge-page">
      <div className="main_title">
        <h1>Badge Details</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
          <button onClick={() => navigate("/achievements")}>
            Manage Achievements & Badges
          </button>{" "}
          - View Badge
        </p>
      </div>

      <div className="cards">
        <div className="view_page">
          <div className="gap_p">
            {/* Badge Image */}
            <div className="w_33">
              <h6>Badge Image</h6>
              <img
                src={badge.image || "/static/images/badge_placeholder.png"}
                alt={badge.title}
                style={{ width: "50%", objectFit: "contain" }}
              />
            </div>

            {/* Title */}
            <div className="w_33">
              <h6>Badge Title</h6>
              <p>{badge.title || "-"}</p>
            </div>

            {/* Description */}
            <div className="w_100">
              <h6>Description</h6>
              <p>{badge.description || "-"}</p>
            </div>

            {/* Criteria Type */}
            <div className="w_33">
              <h6>Criteria Type</h6>
              <p>{getCriteriaTypeLabel(badge)}</p>
            </div>

            {/* Target Value */}
            <div className="w_33">
              <h6>Target Value</h6>
              <p>{getTargetValue(badge)}</p>
            </div>

            {/* Created Date */}
            {badge.createdAt && (
              <div className="w_33">
                <h6>Created Date</h6>
                <p>{formatDate(badge.createdAt)}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewBadge;
